import React, {useEffect, useMemo, useState} from "react"
// MUI
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, InputBase, Typography,
} from "@mui/material"
import {CloseRounded, SearchRounded, SellOutlined} from "@mui/icons-material"
// Data
import {useData} from "@/context/PackagingDataProvider"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"

// Manage the brands an app serves. Modern list-picker: search on top,
// selectable brand rows (whole row toggles), live selection count, quiet
// footer actions — per the Rémi design system.
export default function AssociatedBrandsDialog(props: any) {

    const {open, onClose, appUuid, t} = props
    const {brands, getBrands, getSingleApp, editAny} = useData()

    const [app, setApp] = useState<any>(null)
    const [search, setSearch] = useState<string>("")
    const [saving, setSaving] = useState<boolean>(false)

    useEffect(() => {
        if (open) {
            setSearch("")
            setApp(null)
            if (!brands) getBrands()
            if (appUuid) getSingleApp(appUuid).then((res: any) => setApp(res))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, appUuid])

    const selected: string[] = app?.brands_id ?? []

    const filteredBrands = useMemo(() => {
        const list = brands ?? []
        const q = search.trim().toLowerCase()
        if (!q) return list
        return list.filter((b: any) => (b.name ?? "").toLowerCase().includes(q))
    }, [brands, search])

    const toggleBrand = (brandId: string) => {
        if (!app) return
        const next = selected.includes(brandId)
            ? selected.filter((id: string) => id !== brandId)
            : [...selected, brandId]
        setApp({...app, brands_id: next})
    }

    const handleSave = () => {
        setSaving(true)
        editAny(app, "app")
            .then((res: any) => {if (res) onClose()})
            .finally(() => setSaving(false))
    }

    const loading = !app || brands === null || brands === undefined

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
            {/* Header */}
            <DialogTitle sx={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1.5}}>
                <Box sx={{minWidth: 0}}>
                    <Typography component={"span"} sx={{fontSize: "18px", fontWeight: 600, display: "block"}}>
                        {t("associated_brands")}
                    </Typography>
                    <Typography component={"span"} sx={{fontSize: "12.5px", color: tk.color.textSecondary}}>
                        {(app?.name ? `${app.name} — ` : "") + t("associated_brands_subtitle")}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label={t("common:close")} sx={{color: tk.color.textTertiary}}>
                    <CloseRounded />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{pb: 0}}>
                {/* Search */}
                <Box
                    sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 1.5, py: 0.75, mb: 1.5,
                        border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm,
                        backgroundColor: tk.color.bg,
                        "&:focus-within": {borderColor: tk.color.borderStrong, backgroundColor: tk.color.surface},
                    }}
                >
                    <SearchRounded sx={{fontSize: 18, color: tk.color.textTertiary}} />
                    <InputBase
                        fullWidth
                        autoFocus
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("search_brands_placeholder")}
                        sx={{fontSize: "13px"}}
                    />
                </Box>

                {/* Brand list */}
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 5}}>
                        <CircularProgress size={26} />
                    </Box>
                ) : (
                    <Box
                        className={"fancyScroll"}
                        sx={{
                            maxHeight: 320, overflowY: "auto",
                            border: `1px solid ${tk.color.borderLight}`,
                            borderRadius: tk.radius.card,
                        }}
                    >
                        {filteredBrands.length === 0 ? (
                            <Box sx={{textAlign: "center", py: 4}}>
                                <SellOutlined sx={{fontSize: 24, color: tk.color.textTertiary, mb: 0.5}} />
                                <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                                    {t("no_elements_found")}
                                </Typography>
                            </Box>
                        ) : filteredBrands.map((brand: any, i: number) => {
                            const isSelected = selected.includes(brand.id)
                            return (
                                <Box
                                    key={brand.id}
                                    onClick={() => toggleBrand(brand.id)}
                                    sx={(theme) => ({
                                        display: "flex", alignItems: "center", gap: 1.25,
                                        px: 1.5, py: 1,
                                        cursor: "pointer",
                                        borderBottom: i < filteredBrands.length - 1 ? `1px solid ${tk.color.borderLight}` : "none",
                                        backgroundColor: isSelected ? tk.color.green50 : "transparent",
                                        transition: "background .15s ease",
                                        "&:hover": {backgroundColor: isSelected ? tk.color.green100 : tk.color.bg},
                                    })}
                                >
                                    {/* Initial avatar */}
                                    <Box
                                        sx={(theme) => ({
                                            width: 30, height: 30, flexShrink: 0,
                                            borderRadius: tk.radius.sm,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: "13px", fontWeight: 700,
                                            color: isSelected ? theme.palette.primary.main : tk.color.textSecondary,
                                            backgroundColor: isSelected ? tk.color.green100 : tk.color.bg,
                                            border: `1px solid ${isSelected ? tk.color.green100 : tk.color.borderLight}`,
                                        })}
                                    >
                                        {(brand.name ?? "?").charAt(0).toUpperCase()}
                                    </Box>
                                    <Typography sx={{flex: 1, fontSize: "13.5px", fontWeight: isSelected ? 600 : 500, color: tk.color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                        {brand.name}
                                    </Typography>
                                    <Checkbox
                                        checked={isSelected}
                                        size={"small"}
                                        tabIndex={-1}
                                        disableRipple
                                        sx={{p: 0.25}}
                                        inputProps={{"aria-label": brand.name}}
                                    />
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </DialogContent>

            {/* Footer: count + actions */}
            <DialogActions sx={{px: 3, py: 2, justifyContent: "space-between"}}>
                <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary}}>
                    {t("brands_selected_count", {num: selected.length, total: (brands ?? []).length})}
                </Typography>
                <Box sx={{display: "flex", gap: 1}}>
                    <Button onClick={onClose} sx={{textTransform: "none", color: tk.color.textSecondary}}>
                        {t("common:cancel")}
                    </Button>
                    <Button
                        variant={"contained"} disableElevation
                        disabled={loading || saving}
                        onClick={handleSave}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}
                    >
                        {t("common:save")}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
